import {
  Box,
  Button,
  CardHeader,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  Divider,
  Tooltip,
  IconButton,
  Autocomplete,
  Chip,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  LinearProgress,
} from '@material-ui/core'
import { UploadMultiFile } from 'src/components/upload'
import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { uploadFile } from '../../api/bed'
import { updateHpo } from '../../api/vcf/hpo'
import {
  Check as CheckIcon,
  Close as CloseIcon,
  BiotechOutlined as BiotechIcon,
  DescriptionOutlined as DescriptionIcon,
  LocalHospitalOutlined as HospitalIcon,
  SearchOutlined as SearchIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons'
import CircularProgress from '@material-ui/core/CircularProgress'
import { green, grey } from '@material-ui/core/colors'
import { styled, alpha } from '@material-ui/core/styles'
import HPOFilterWrapper from 'src/components/HPOFilterWrapper'
import DiseaseFilterWrapper from 'src/components/DiseaseFilterWrapper'
import useAuth from 'src/hooks/useAuth'
import { useManageUser } from 'src/api/user/manage'
import diseaseService from 'src/api/filters/diseaseService'

let cancelTokenSource = null

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '24px 32px',
  backgroundColor: 'rgba(0, 0, 0, 0.25)',
  backdropFilter: 'blur(12px)',
  gap: '12px',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
}))

const CLINICAL_HISTORY_PLACEHOLDER =
  'Enter as much relevant clinical details as possible. Such as patient symptoms (also select HPOs where appropriate), age of onset, family history, previous diagnoses, treatments, or any notable clinical events.'

const USER_GUIDANCE_PLACEHOLDER =
  'Provide specific analysis instructions, highlight points requiring special attention, or suggest particular diseases, genes, or pathways for detailed consideration.'

// Updated styled components for a cleaner look
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1a2035',
    backdropFilter: 'blur(10px)',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    maxWidth: '800px',
    color: '#fff',
  },
  '& .MuiInputBase-root': {
    color: '#fff',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  '& .MuiMenuItem-root': {
    color: '#fff',
  },
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(3),
  background: 'linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)',
  '& .MuiTypography-root': {
    color: '#fff',
    fontSize: '1.5rem',
    fontWeight: 500,
  },
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  maxHeight: '75vh',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
  },
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
  '& .MuiTypography-root': {
    color: '#00e676',
    fontSize: '1.1rem',
    fontWeight: 500,
    letterSpacing: '0.05em',
  },
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#00e676',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#00e676',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00e676',
  },
}))

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  padding: '10px 24px',
  fontSize: '0.9rem',
  transition: 'all 0.2s ease-in-out',
  minWidth: '140px',
  '&.MuiButton-containedPrimary': {
    background: 'linear-gradient(135deg, #00e676 0%, #00c853 100%)',
    boxShadow: '0 8px 16px -4px rgba(0, 230, 118, 0.25)',
    '&:hover': {
      background: 'linear-gradient(135deg, #00c853 0%, #009624 100%)',
      boxShadow: '0 12px 20px -4px rgba(0, 230, 118, 0.35)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&.Mui-disabled': {
      background: 'rgba(255, 255, 255, 0.12)',
      color: 'rgba(255, 255, 255, 0.3)',
    },
  },
  '&.MuiButton-outlined': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    backdropFilter: 'blur(8px)',
    '&:hover': {
      borderColor: '#00e676',
      backgroundColor: 'rgba(0, 230, 118, 0.08)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
}))

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-checked': {
      color: '#00e676',
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(0, 230, 118, 0.5)',
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}))

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  '& .MuiRadio-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-checked': {
      color: '#00e676',
    },
  },
  '& .MuiFormControlLabel-label': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
}))

const SectionBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '24px',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(8px)',
  '&:last-child': {
    marginBottom: 0,
  },
}))

const StyledOptionCard = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  '&:hover:not(.disabled)': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&.active': {
    borderColor: '#00e676',
    backgroundColor: 'rgba(0, 230, 118, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(0, 230, 118, 0.08)',
    },
  },
  '&.disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}))

const OptionIcon = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  '& svg': {
    fontSize: '24px',
    color: '#00e676',
  },
  '&.disabled svg': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
}))

// Tab Panel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analysis-tabpanel-${index}`}
      aria-labelledby={`analysis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index) {
  return {
    id: `analysis-tab-${index}`,
    'aria-controls': `analysis-tabpanel-${index}`,
  }
}

export const NewAnnotationDialog = function ({
  open,
  onClose,
  onClickAnnotate,
  fileType,
  title,
  bedFiles,
  bedFilesApi,
  data,
  currentId,
  vcfId,
}) {
  // Tab control
  const [activeTab, setActiveTab] = useState(0)
  const { user } = useAuth()
  const { self } = useManageUser(user)
  const userDetails = self.data

  // First tab state
  const [type, setType] = useState('WholeGenome') // Can be WholeGenome or Targeted
  const [targetedType, setTargetedType] = useState('WholeExome') // Can be  '' or WholeExome or ClinicalExome
  const [reference, setReference] = useState('GRCh38')
  const [analysisType, setAnalysisType] = useState('Germline')

  // Second tab state
  const [runCNV, setRunCNV] = useState(false) // disabled for now
  const [runAI, setRunAI] = useState(false)
  const [runSV, setRunSV] = useState(false)

  // Third tab state
  const [hpos, setHpos] = useState('')
  const [diseases, setDiseases] = useState('')
  const [clinicalHistory, setClinicalHistory] = useState('')
  const [userGuidance, setUserGuidance] = useState('')

  const [bedUploadOpen, setBedUploadOpen] = useState(false)
  const [fileQueue, setFileQueue] = useState([])
  const [state, setState] = useState('IDLE')
  const [uploadProgress, setUploadProgress] = useState(0)

  const [fileNames, setFileNames] = useState([])
  const [references, setReferences] = useState([])
  const [normalSample, setNormalSample] = useState('')

  // Only the FASTQ form has an Analysis Type field, but the state outlives it: the dialog
  // stays mounted across files, so a VCF opened after a Tumor Only FASTQ still holds
  // 'TumorOnly'. Every other form is a germline request.
  const requestAnalysisType = fileType === 'FASTQ' ? analysisType : 'Germline'

  // AI prioritisation ranks variants by inherited-disease phenotype match, which has no
  // tumour meaning, and the backend rejects runAI on a tumour run with a 400. Keep the
  // option off and locked for Tumor Only and Tumor/Normal.
  const aiUnavailable = requestAnalysisType === 'TumorOnly' || requestAnalysisType === 'TumorNormal'
  useEffect(() => {
    if (aiUnavailable && runAI) setRunAI(false)
  }, [aiUnavailable, runAI])

  const [isAnnotating, setIsAnnotating] = useState(false)
  const [annotateSuccess, setAnnotateSuccess] = useState(false)
  const [requestInProgress, setRequestInProgress] = useState(false)
  const filteredData = data.filter(
    (item) =>
      item.fastq_pair_id &&
      item.fastq_file_1 &&
      item.fastq_file_2 &&
      // !item.is_annotated &&
      item.fastq_pair_id !== currentId,
  )

  const [panel, setPanel] = useState(bedFiles.length > 0 ? bedFiles[0].name : '')

  // Step 2: Update panel when bedFiles changes (e.g., after fetching data)
  useEffect(() => {
    if (bedFiles.length > 0 && panel === '') {
      setPanel(bedFiles[0].name)
    }
  }, [bedFiles])

  const timeoutRef = useRef(null)

  // Add cleanup effect
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleDropMultiFile = async (filesToUpload) => {
    setFileQueue(filesToUpload)
    setFileNames(filesToUpload.map((file) => file.name))
    setReferences(filesToUpload.map(() => 'GRCh38'))
    setState('READY_TO_UPLOAD')
  }

  const handleUploadFiles = async () => {
    if (fileQueue.length === 0) return
    setState('UPLOADING')
  }

  const handleRemoveAll = () => {
    setFileQueue([])
    setFileNames([])
    setReferences([])
    setState('IDLE')
  }

  const handleRemove = (file) => {
    const index = fileQueue.indexOf(file)
    if (index === -1) return

    setFileQueue(fileQueue.filter((f) => f !== file))
    setFileNames(fileNames.filter((_, i) => i !== index))
    setReferences(references.filter((_, i) => i !== index))

    if (fileQueue.length === 1) {
      setState('IDLE')
    }
  }

  const handleCancel = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Cancelled by user')
    }
    handleRemoveAll()
  }

  const NamingComponent = ({ file, index, fileNames, setFileNames, references, setReferences }) => {
    const handleFileNameChange = (value, index) => {
      const newFileNames = [...fileNames]
      newFileNames[index] = value
      setFileNames(newFileNames)
    }

    const handleReferenceChange = (value, index) => {
      const newReferences = [...references]
      newReferences[index] = value
      setReferences(newReferences)
    }
    return file ? (
      <Box>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                key={`fname-${file.name}`}
                label="File name"
                fullWidth
                value={fileNames[index]}
                onChange={(e) => handleFileNameChange(e.target.value, index)}
              ></TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                key={`fref-${file.name}`}
                label="Reference"
                select
                fullWidth
                value={references[index]}
                onChange={(e) => handleReferenceChange(e.target.value, index)}
              >
                <MenuItem value={'GRCh38'}>GRCh38</MenuItem>
                <MenuItem value={'GRCh37'}>GRCh37</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Box>
    ) : null
  }

  useEffect(() => {
    const uploadFilesStateMachine = async () => {
      if (state === 'UPLOADING' && fileQueue.length > 0) {
        try {
          setUploadProgress(0)
          cancelTokenSource = axios.CancelToken.source()

          await uploadFile(
            fileQueue[0],
            (value) => setUploadProgress(value),
            cancelTokenSource.token,
            fileNames[0],
            references[0],
          )

          setFileQueue(fileQueue.slice(1))
          setFileNames(fileNames.slice(1))
          setReferences(references.slice(1))
          await bedFilesApi.refresh()

          if (fileQueue.length === 1) {
            setState('IDLE')
            setBedUploadOpen(false)
          }
        } catch (error) {
          console.error('Error uploading file:', error)
          setState('IDLE')
        }
      }
    }

    uploadFilesStateMachine()
  }, [state, fileQueue])

  const handleClickAnnotate = async () => {
    if (requestInProgress) {
      return
    }
    try {
      setRequestInProgress(true)
      setIsAnnotating(true)
      setAnnotateSuccess(false)

      var realType = type,
        realTargetedType = targetedType,
        realPanel = panel,
        realNormalSample = normalSample

      if (type === 'WholeGenome') {
        realTargetedType = ''
        realPanel = ''
      }

      if (requestAnalysisType !== 'TumorNormal') {
        realNormalSample = ''
      }

      // Safely transform diseases data
      const transformedDiseases = {
        diseases: Array.isArray(diseases) ? diseases.map((d) => d.disease).filter(Boolean) : [],
        diseaseIds: Array.isArray(diseases) ? diseases.map((d) => d.omim).filter(Boolean) : [],
        diseaseKeys: Array.isArray(diseases) ? diseases.map((d) => d.key).filter(Boolean) : [],
      }

      const result = await onClickAnnotate({
        type: realType,
        panel: realPanel,
        targetedType: realTargetedType,
        analysisType: requestAnalysisType,
        reference,
        isSnp: true,
        isCnv: false,
        runAI: runAI && !aiUnavailable,
        runCnv: fileType === 'ONT_BAM' ? runCNV : false,
        runSv: fileType === 'ONT_BAM' || fileType === 'PACBIO_BAM' ? runSV : false,
        normalSampleId: realNormalSample,
        hpos: hpos?.hpos || [],
        hpoNames: hpos?.hpoNames || [],
        ...transformedDiseases,
        clinicalHistory: clinicalHistory || '',
        userGuidance: userGuidance || '',
      })

      if (result !== false) {
        if (hpos?.hpos?.length > 0) {
          try {
            const fileId = vcfId || currentId
            const hpoListWithUserData = hpos.hpos.map((hpoId, index) => ({
              value: hpoId,
              label: `${hpos.hpoNames[index]} (${hpoId})`,
              firstName: userDetails?.first_name || '',
              lastName: userDetails?.last_name || '',
              timestamp: new Date().toISOString(),
              association: {
                name: title,
                type: 'Sample',
              },
            }))
            await updateHpo(fileId, hpoListWithUserData)
          } catch (hpoError) {
            console.error('Failed to save HPO terms:', hpoError)
          }
        }

        if (Array.isArray(diseases) && diseases.length > 0) {
          try {
            const fileId = vcfId || currentId
            const diseaseKeys = diseases.map((d) => d.key).filter(Boolean)
            if (diseaseKeys.length > 0) {
              await diseaseService.updateDiseaseList(fileId, diseaseKeys)
            }
          } catch (diseaseError) {
            console.error('Failed to save diseases:', diseaseError)
          }
        }

        setAnnotateSuccess(true)
        await new Promise((resolve) => {
          timeoutRef.current = setTimeout(resolve, 2000)
        })
        if (timeoutRef.current) {
          setIsAnnotating(false)
          onClose()
        }
      } else {
        setIsAnnotating(false)
      }
    } catch (error) {
      console.error('Annotation failed:', error)
      setIsAnnotating(false)
      setAnnotateSuccess(false)
    } finally {
      setRequestInProgress(false)
    }
  }

  const handleModalClose = () => {
    setBedUploadOpen(false)
    onClose()
  }

  const AnnotateButton = () => {
    const hasValidationErrors =
      runAI &&
      (!hpos ||
        !hpos.hpos ||
        hpos.hpos.length === 0 ||
        !Array.isArray(diseases) ||
        diseases.length === 0 ||
        !clinicalHistory)
    const disableButton = requestInProgress || hasValidationErrors

    return (
      <Box sx={{ position: 'relative' }}>
        <Tooltip
          title={hasValidationErrors ? 'Please fill all required fields for AI analysis' : ''}
          arrow
          placement="top"
        >
          <span>
            <StyledButton
              onClick={handleClickAnnotate}
              variant="contained"
              color="primary"
              disabled={disableButton}
              sx={{
                position: 'relative',
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                },
              }}
            >
              {annotateSuccess ? (
                <>
                  Done
                  <CheckIcon sx={{ ml: 1, fontSize: 18 }} />
                </>
              ) : (
                'Annotate'
              )}
            </StyledButton>
          </span>
        </Tooltip>
        {isAnnotating && (
          <CircularProgress
            size={24}
            sx={{
              color: '#00e676',
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
            }}
          />
        )}
      </Box>
    )
  }

  const renderAnalysisOptions = () => (
    <SectionBox>
      <SectionHeader>
        <Typography>Analysis Options</Typography>
      </SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledOptionCard className="disabled" onClick={(e) => e.preventDefault()}>
            <OptionIcon className="disabled">
              <BiotechIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Run CNV pipeline
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                  }}
                >
                  Coming Soon
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Analyze Copy Number Variations in your genome
              </Typography>
            </Box>
          </StyledOptionCard>
        </Grid>
        <Grid item xs={12}>
          <StyledOptionCard
            className={aiUnavailable ? 'disabled' : runAI ? 'active' : ''}
            onClick={() => !aiUnavailable && setRunAI(!runAI)}
          >
            <OptionIcon className={aiUnavailable ? 'disabled' : ''}>
              <SearchIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5 }}>
                Run AI variant prioritization
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {aiUnavailable
                  ? 'Not available for somatic analyses'
                  : 'Use AI to identify and prioritize significant variants'}
              </Typography>
            </Box>
            <Switch
              checked={runAI}
              disabled={aiUnavailable}
              inputProps={{ 'aria-label': 'Run AI variant prioritization' }}
              onChange={(e) => setRunAI(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': {
                    color: '#00e676',
                    '& + .MuiSwitch-track': {
                      backgroundColor: 'rgba(0, 230, 118, 0.5)',
                    },
                  },
                },
                '& .MuiSwitch-track': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            />
          </StyledOptionCard>
        </Grid>
      </Grid>
    </SectionBox>
  )

  const renderOntAnalysisOptions = () => (
    <SectionBox>
      <SectionHeader>
        <Typography>Analysis Options</Typography>
      </SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledOptionCard className={runCNV ? 'active' : ''} onClick={() => setRunCNV(!runCNV)}>
            <OptionIcon>
              <BiotechIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5 }}>
                Run CNV analysis
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Detect Copy Number Variations using Spectre
              </Typography>
            </Box>
            <Switch
              checked={runCNV}
              onChange={(e) => setRunCNV(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': {
                    color: '#00e676',
                    '& + .MuiSwitch-track': { backgroundColor: 'rgba(0, 230, 118, 0.5)' },
                  },
                },
                '& .MuiSwitch-track': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            />
          </StyledOptionCard>
        </Grid>
        <Grid item xs={12}>
          <StyledOptionCard className={runSV ? 'active' : ''} onClick={() => setRunSV(!runSV)}>
            <OptionIcon>
              <BiotechIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5 }}>
                Run SV analysis
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Detect Structural Variants using Sniffles2
              </Typography>
            </Box>
            <Switch
              checked={runSV}
              onChange={(e) => setRunSV(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': {
                    color: '#00e676',
                    '& + .MuiSwitch-track': { backgroundColor: 'rgba(0, 230, 118, 0.5)' },
                  },
                },
                '& .MuiSwitch-track': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            />
          </StyledOptionCard>
        </Grid>
        <Grid item xs={12}>
          <StyledOptionCard className={runAI ? 'active' : ''} onClick={() => setRunAI(!runAI)}>
            <OptionIcon>
              <SearchIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5 }}>
                Run AI variant prioritization
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Use AI to identify and prioritize significant variants
              </Typography>
            </Box>
            <Switch
              checked={runAI}
              onChange={(e) => setRunAI(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': {
                    color: '#00e676',
                    '& + .MuiSwitch-track': { backgroundColor: 'rgba(0, 230, 118, 0.5)' },
                  },
                },
                '& .MuiSwitch-track': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            />
          </StyledOptionCard>
        </Grid>
      </Grid>
    </SectionBox>
  )

  const renderVCFForm = () => (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Typography variant="h6">Make annotation for {title}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <SectionBox>
          <SectionHeader>
            <Typography>Sequence</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                label="Reference Genome"
                select
                fullWidth
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              >
                <MenuItem value={'GRCh38'}>GRCh38</MenuItem>
                <MenuItem value={'GRCh37'}>GRCh37</MenuItem>
              </StyledTextField>
            </Grid>
          </Grid>
        </SectionBox>

        {renderAnalysisOptions()}

        <SectionBox>
          <SectionHeader>
            <Typography>Clinical Information</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HPOFilterWrapper value={hpos} onChange={setHpos} arrayOutput={true} label="HPO Terms" />
              {runAI && (!hpos || !hpos.hpos || hpos.hpos.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <DiseaseFilterWrapper
                value={diseases}
                onChange={(newDiseases) => {
                  setDiseases(Array.isArray(newDiseases) ? newDiseases : [])
                }}
                arrayOutput={true}
                label="Diseases"
                showInheritance={false}
              />
              {runAI && (!Array.isArray(diseases) || diseases.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Clinical History"
                fullWidth
                multiline
                rows={4}
                value={clinicalHistory}
                onChange={(e) => setClinicalHistory(e.target.value)}
                placeholder={CLINICAL_HISTORY_PLACEHOLDER}
                error={runAI && !clinicalHistory}
                helperText={runAI && !clinicalHistory ? 'Required for AI analysis' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="User Guidance"
                fullWidth
                multiline
                rows={4}
                value={userGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder={USER_GUIDANCE_PLACEHOLDER}
                helperText={
                  runAI
                    ? 'For best AI prioritization results, please fill out all clinical information fields above.'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </SectionBox>
      </StyledDialogContent>
      <StyledDialogActions>
        <Box sx={{ flex: 1 }} />
        <StyledButton onClick={handleModalClose} variant="outlined">
          Cancel
        </StyledButton>
        <AnnotateButton />
      </StyledDialogActions>
    </StyledDialog>
  )

  const renderFastqForm = () => (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Typography variant="h6">Make annotation for {title}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <SectionBox>
          <SectionHeader>
            <Typography>Sequence</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledRadioGroup row value={type} onChange={(e) => setType(e.target.value)} sx={{ mb: 3 }}>
                <FormControlLabel value="WholeGenome" control={<Radio />} label="Whole Genome" />
                <FormControlLabel value="Targeted" control={<Radio />} label="Targeted" />
              </StyledRadioGroup>
            </Grid>

            {type === 'Targeted' && (
              <>
                <Grid item xs={6}>
                  <StyledTextField
                    label="Capture Kit"
                    value={panel}
                    fullWidth
                    onChange={(e) => setPanel(e.target.value)}
                    select
                  >
                    {bedFiles.map((bedFile, index) => (
                      <MenuItem key={bedFile.name} value={bedFile.name} disabled={index === 0 || index === 5}>
                        {bedFile.visible_name || bedFile.original_name}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Grid>
                <Grid item xs={6}>
                  <StyledTextField
                    label="Targeted Type"
                    value={targetedType}
                    fullWidth
                    onChange={(e) => setTargetedType(e.target.value)}
                    select
                  >
                    <MenuItem value="WholeExome">Whole Exome</MenuItem>
                    <MenuItem value="ClinicalExome">Clinical Exome</MenuItem>
                  </StyledTextField>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00e676' }}>
                Analysis Settings
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <StyledTextField
                label="Analysis Type"
                value={analysisType}
                fullWidth
                onChange={(e) => setAnalysisType(e.target.value)}
                select
              >
                <MenuItem value="Germline">Germline</MenuItem>
                <MenuItem value="TumorOnly">Tumor Only</MenuItem>
                <MenuItem value="TumorNormal">Tumor/Normal</MenuItem>
              </StyledTextField>
            </Grid>
            {analysisType === 'TumorNormal' && (
              <Grid item xs={6}>
                <StyledTextField
                  label="Normal Sample"
                  value={normalSample}
                  fullWidth
                  onChange={(e) => setNormalSample(e.target.value)}
                  select
                >
                  {filteredData.map((item) => (
                    <MenuItem key={item.fastq_pair_id} value={item.fastq_pair_id}>
                      {item.sample_name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </Grid>
            )}
          </Grid>
        </SectionBox>

        {renderAnalysisOptions()}

        <SectionBox>
          <SectionHeader>
            <Typography>Clinical Information</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HPOFilterWrapper value={hpos} onChange={setHpos} arrayOutput={true} label="HPO Terms" />
              {runAI && (!hpos || !hpos.hpos || hpos.hpos.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <DiseaseFilterWrapper
                value={diseases}
                onChange={(newDiseases) => {
                  setDiseases(Array.isArray(newDiseases) ? newDiseases : [])
                }}
                arrayOutput={true}
                label="Diseases"
                showInheritance={false}
              />
              {runAI && (!Array.isArray(diseases) || diseases.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Clinical History"
                fullWidth
                multiline
                rows={4}
                value={clinicalHistory}
                onChange={(e) => setClinicalHistory(e.target.value)}
                placeholder={CLINICAL_HISTORY_PLACEHOLDER}
                error={runAI && !clinicalHistory}
                helperText={runAI && !clinicalHistory ? 'Required for AI analysis' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="User Guidance"
                fullWidth
                multiline
                rows={4}
                value={userGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder={USER_GUIDANCE_PLACEHOLDER}
                helperText={
                  runAI
                    ? 'For best AI prioritization results, please fill out all clinical information fields above.'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </SectionBox>
      </StyledDialogContent>
      <StyledDialogActions>
        <Box sx={{ flex: 1 }} />
        <StyledButton onClick={handleModalClose} variant="outlined">
          Cancel
        </StyledButton>
        {!bedUploadOpen && (
          <StyledButton onClick={() => setBedUploadOpen(true)} variant="outlined">
            Upload New Capture Kit
          </StyledButton>
        )}
        <AnnotateButton />
      </StyledDialogActions>
      {renderUploadModal()}
    </StyledDialog>
  )

  const renderOntBamForm = () => (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Typography variant="h6">Make annotation for {title}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <SectionBox>
          <SectionHeader>
            <Typography>Sequence</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                ONT Long-Read Sequencing &mdash; Reference Genome: GRCh38
              </Typography>
            </Grid>
          </Grid>
        </SectionBox>

        {renderOntAnalysisOptions()}

        <SectionBox>
          <SectionHeader>
            <Typography>Clinical Information</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HPOFilterWrapper value={hpos} onChange={setHpos} arrayOutput={true} label="HPO Terms" />
              {runAI && (!hpos || !hpos.hpos || hpos.hpos.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <DiseaseFilterWrapper
                value={diseases}
                onChange={(newDiseases) => {
                  setDiseases(Array.isArray(newDiseases) ? newDiseases : [])
                }}
                arrayOutput={true}
                label="Diseases"
                showInheritance={false}
              />
              {runAI && (!Array.isArray(diseases) || diseases.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Clinical History"
                fullWidth
                multiline
                rows={4}
                value={clinicalHistory}
                onChange={(e) => setClinicalHistory(e.target.value)}
                placeholder={CLINICAL_HISTORY_PLACEHOLDER}
                error={runAI && !clinicalHistory}
                helperText={runAI && !clinicalHistory ? 'Required for AI analysis' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="User Guidance"
                fullWidth
                multiline
                rows={4}
                value={userGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder={USER_GUIDANCE_PLACEHOLDER}
                helperText={
                  runAI
                    ? 'For best AI prioritization results, please fill out all clinical information fields above.'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </SectionBox>
      </StyledDialogContent>
      <StyledDialogActions>
        <Box sx={{ flex: 1 }} />
        <StyledButton onClick={handleModalClose} variant="outlined">
          Cancel
        </StyledButton>
        <AnnotateButton />
      </StyledDialogActions>
    </StyledDialog>
  )

  // --- Modern Upload Modal for Capture Kit ---
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const handleCloseUploadModal = () => {
    setBedUploadOpen(false)
    setFileQueue([])
    setFileNames([])
    setReferences([])
    setUploadProgress(0)
    setUploadError('')
    setUploadSuccess(false)
    setIsUploading(false)
    setState('IDLE')
  }

  const handleDropCaptureKit = (files) => {
    setFileQueue(files)
    setFileNames(files.map((file) => file.name))
    setReferences(files.map(() => 'GRCh38'))
    setUploadError('')
    setUploadSuccess(false)
    setIsUploading(false)
    setState('READY_TO_UPLOAD')
  }

  const handleStartUpload = async () => {
    if (!fileQueue.length || isUploading) return
    setIsUploading(true)
    setUploadError('')
    setUploadSuccess(false)
    setUploadProgress(0)
    try {
      cancelTokenSource = axios.CancelToken.source()
      await uploadFile(
        fileQueue[0],
        (value) => setUploadProgress(value),
        cancelTokenSource.token,
        fileNames[0],
        references[0],
      )
      setUploadSuccess(true)
      setUploadError('')
      setFileQueue([])
      setFileNames([])
      setReferences([])
      setState('IDLE')
      await bedFilesApi.refresh()
      setTimeout(() => {
        handleCloseUploadModal()
      }, 1200)
    } catch (error) {
      setUploadError('Upload failed. Please try again.')
      setUploadSuccess(false)
      setIsUploading(false)
      setState('IDLE')
    }
  }

  // --- Upload Modal UI ---
  const renderUploadModal = () => (
    <Dialog open={bedUploadOpen} onClose={handleCloseUploadModal} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Upload New Capture Kit</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: '2px dashed #00e676',
            borderRadius: 2,
            p: 3,
            mb: 2,
            textAlign: 'center',
            bgcolor: 'rgba(0,230,118,0.03)',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: '#00c853' },
          }}
          tabIndex={0}
          aria-label="Drop BED file here or click to select"
          onClick={() => document.getElementById('bed-upload-input').click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') document.getElementById('bed-upload-input').click()
          }}
        >
          <input
            id="bed-upload-input"
            type="file"
            accept=".bed"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleDropCaptureKit(Array.from(e.target.files))
              }
            }}
          />
          <Typography variant="body1" sx={{ color: '#00e676', fontWeight: 500 }}>
            Drag & drop BED file here, or click to select
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Only .bed files are accepted
          </Typography>
        </Box>
        {fileQueue.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              File to upload:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.04)',
                mb: 1,
              }}
            >
              <Typography variant="body2">{fileNames[0]}</Typography>
              <TextField
                select
                size="small"
                value={references[0]}
                onChange={(e) => setReferences([e.target.value])}
                sx={{ minWidth: 100, ml: 2 }}
                label="Reference"
              >
                <MenuItem value="GRCh38">GRCh38</MenuItem>
                <MenuItem value="GRCh37">GRCh37</MenuItem>
              </TextField>
            </Box>
            {isUploading && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 2 }} />
                <Typography variant="caption" sx={{ color: '#00e676' }}>
                  {uploadProgress}%
                </Typography>
              </Box>
            )}
            {uploadError && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {uploadError}
              </Typography>
            )}
            {uploadSuccess && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                Upload successful!
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseUploadModal} color="inherit" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleStartUpload}
          color="primary"
          variant="contained"
          disabled={isUploading || !fileQueue.length || uploadSuccess}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )

  const renderPacBioAnalysisOptions = () => (
    <SectionBox>
      <SectionHeader>
        <Typography>Analysis Options</Typography>
      </SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledOptionCard className={runSV ? 'active' : ''} onClick={() => setRunSV(!runSV)}>
            <OptionIcon>
              <BiotechIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5 }}>
                Run SV analysis
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Detect Structural Variants using pbsv
              </Typography>
            </Box>
            <Switch
              checked={runSV}
              onChange={(e) => setRunSV(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': {
                    color: '#00e676',
                    '& + .MuiSwitch-track': { backgroundColor: 'rgba(0, 230, 118, 0.5)' },
                  },
                },
                '& .MuiSwitch-track': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            />
          </StyledOptionCard>
        </Grid>
        <Grid item xs={12}>
          <StyledOptionCard className={runAI ? 'active' : ''} onClick={() => setRunAI(!runAI)}>
            <OptionIcon>
              <SearchIcon />
            </OptionIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5 }}>
                Run AI Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                AI-powered variant prioritization
              </Typography>
            </Box>
            <Switch
              checked={runAI}
              onChange={(e) => setRunAI(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': {
                    color: '#00e676',
                    '& + .MuiSwitch-track': { backgroundColor: 'rgba(0, 230, 118, 0.5)' },
                  },
                },
                '& .MuiSwitch-track': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            />
          </StyledOptionCard>
        </Grid>
      </Grid>
    </SectionBox>
  )

  const renderPacBioBamForm = () => (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Typography variant="h6">Make annotation for {title}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <SectionBox>
          <SectionHeader>
            <Typography>Sequence</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                PacBio HiFi Long-Read Sequencing &mdash; Reference Genome: GRCh38
              </Typography>
            </Grid>
          </Grid>
        </SectionBox>

        {renderPacBioAnalysisOptions()}

        <SectionBox>
          <SectionHeader>
            <Typography>Clinical Information</Typography>
          </SectionHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HPOFilterWrapper value={hpos} onChange={setHpos} arrayOutput={true} label="HPO Terms" />
              {runAI && (!hpos || !hpos.hpos || hpos.hpos.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <DiseaseFilterWrapper
                value={diseases}
                onChange={(newDiseases) => {
                  setDiseases(Array.isArray(newDiseases) ? newDiseases : [])
                }}
                arrayOutput={true}
                label="Diseases"
                showInheritance={false}
              />
              {runAI && (!Array.isArray(diseases) || diseases.length === 0) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Required for AI analysis
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Clinical History"
                fullWidth
                multiline
                rows={4}
                value={clinicalHistory}
                onChange={(e) => setClinicalHistory(e.target.value)}
                placeholder={CLINICAL_HISTORY_PLACEHOLDER}
                error={runAI && !clinicalHistory}
                helperText={runAI && !clinicalHistory ? 'Required for AI analysis' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="User Guidance"
                fullWidth
                multiline
                rows={4}
                value={userGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder={USER_GUIDANCE_PLACEHOLDER}
                helperText={
                  runAI
                    ? 'For best AI prioritization results, please fill out all clinical information fields above.'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </SectionBox>
      </StyledDialogContent>
      <StyledDialogActions>
        <Box sx={{ flex: 1 }} />
        <StyledButton onClick={handleModalClose} variant="outlined">
          Cancel
        </StyledButton>
        <AnnotateButton />
      </StyledDialogActions>
    </StyledDialog>
  )

  return fileType === 'VCF'
    ? renderVCFForm()
    : fileType === 'ONT_BAM'
    ? renderOntBamForm()
    : fileType === 'PACBIO_BAM'
    ? renderPacBioBamForm()
    : renderFastqForm()
}
